import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useCallback } from "react";
import type { paths } from "../../api/generated/api-types";
import { logger } from "../../observability/LoggerFactory";

type ResetResponse =
  paths["/users/{userId}/reset-password"]["post"]["responses"]["200"]["content"]["application/json"];

type ResetState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "success"; login: string; generatedPassword: string }
  | { kind: "error"; message: string };

export interface UseResetUserPasswordResult {
  state: ResetState;
  resetPassword: (userId: string) => void;
  reset: () => void;
}

export function useResetUserPassword(): UseResetUserPasswordResult {
  const [state, setState] = useState<ResetState>({ kind: "idle" });

  const resetPassword = useCallback((userId: string) => {
    void (async () => {
      setState({ kind: "loading" });
      try {
        const response = await fetchWithRefresh(`/api/users/${userId}/reset-password`, { method: "POST" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const data = (await response.json()) as ResetResponse;
        setState({ kind: "success", login: data.login, generatedPassword: data.generatedPassword });
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Unknown error";
        logger.error({ err: message }, "Failed to reset user password");
        setState({ kind: "error", message });
      }
    })();
  }, []);

  const reset = useCallback(() => {
    setState({ kind: "idle" });
  }, []);

  return { state, resetPassword, reset };
}
